import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, CurrencyExchange, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getRMBExchangeRates, createRMBExchangeRate, updateRMBExchangeRate, deleteRMBExchangeRate } from '../../../services/api';

const defaultForm = { wallet_type: 'foreign', min_amount: '', max_amount: '', rate: '', is_active: true };

const ExchangeRate = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [filter, setFilter] = useState({ wallet_type: '', is_active: '' });
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const fetchData = useCallback(async (pageArg, rowsArg, filterArg = filter) => {
    setLoading(true);
    try {
      const params = {
        page: pageArg + 1,
        page_size: rowsArg,
        ...filterArg
      };
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      const res = await getRMBExchangeRates(params);
      const results = res.results || res;
      const totalCount = res.count || res.total || results.length;
      const ratesArray = Array.isArray(results) ? results : [];
      console.log('Fetched exchange rates:', ratesArray);
      if (ratesArray.length > 0) {
        console.log('First rate sample:', ratesArray[0]);
        console.log('min_amount:', ratesArray[0].min_amount, typeof ratesArray[0].min_amount);
        console.log('rate:', ratesArray[0].rate, typeof ratesArray[0].rate);
      }
      setRates(ratesArray);
      setTotal(totalCount);
    } catch (e) {
      console.error('Error fetching exchange rates:', e);
      setSnackbar({ open: true, message: 'Error fetching exchange rates', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchData(page, rowsPerPage);
  }, [page, rowsPerPage, fetchData]);

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const handleRefresh = () => { setPage(0); fetchData(0, rowsPerPage); };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilter({ wallet_type: '', is_active: '' });
    setPage(0);
  };

  const hasActiveFilters = filter.wallet_type !== '' || filter.is_active !== '';

  // CRUD handlers
  const handleOpen = (rate = null) => {
    setEditId(rate ? (rate.sid || rate.id) : null);
    setForm(rate ? {
      wallet_type: rate.wallet_type || 'foreign',
      min_amount: rate.min_amount?.toString() || '',
      max_amount: rate.max_amount?.toString() || '',
      rate: rate.rate?.toString() || '',
      is_active: rate.is_active !== undefined ? rate.is_active : true,
    } : defaultForm);
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setForm(defaultForm); setEditId(null); };

  const handleSave = async () => {
    if (!form.min_amount || parseFloat(form.min_amount) <= 0) {
      setSnackbar({ open: true, message: 'Minimum amount is required and must be greater than 0', severity: 'error' });
      return;
    }
    if (form.max_amount && parseFloat(form.max_amount) <= parseFloat(form.min_amount)) {
      setSnackbar({ open: true, message: 'Maximum amount must be greater than minimum amount', severity: 'error' });
      return;
    }
    if (!form.rate || parseFloat(form.rate) <= 0) {
      setSnackbar({ open: true, message: 'Exchange rate is required and must be greater than 0', severity: 'error' });
      return;
    }
    try {
      const data = {
        wallet_type: form.wallet_type,
        min_amount: parseFloat(form.min_amount),
        max_amount: form.max_amount ? parseFloat(form.max_amount) : null,
        rate: parseFloat(form.rate),
        is_active: form.is_active,
      };
      if (editId) {
        await updateRMBExchangeRate(editId, data);
        setSnackbar({ open: true, message: 'Exchange rate updated successfully', severity: 'success' });
      } else {
        await createRMBExchangeRate(data);
        setSnackbar({ open: true, message: 'Exchange rate added successfully', severity: 'success' });
      }
      handleClose();
      fetchData(page, rowsPerPage);
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.detail || Object.values(e.response?.data || {}).flat().join(', ') || 'Error saving exchange rate', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRMBExchangeRate(id);
      setSnackbar({ open: true, message: 'Exchange rate deleted successfully', severity: 'success' });
      setDeleteModal({ open: false, id: null });
      fetchData(page, rowsPerPage);
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.detail || 'Error deleting exchange rate', severity: 'error' });
    }
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false,
    },
    {
      field: 'wallet_type',
      headerName: 'Wallet Type',
      width: 150,
      renderCell: (params) => {
        const walletType = params.row.wallet_type;
        let label = 'Unknown';
        let color = 'default';
        
        if (walletType === 'foreign') {
          label = 'Foreign Alipay';
          color = 'primary';
        } else if (walletType === 'chinese') {
          label = 'Chinese Alipay';
          color = 'secondary';
        } else if (walletType === 'chinese_bank') {
          label = 'Chinese Bank';
          color = 'success';
        }
        
        return (
          <Chip
            label={label}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      field: 'min_amount',
      headerName: 'Min Amount (RMB)',
      width: 140,
      type: 'number',
      renderCell: (params) => {
        if (!params || !params.row) return '0';
        const value = params.row.min_amount;
        if (value == null || value === '') return '0';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '0';
        return numValue.toLocaleString();
      },
    },
    {
      field: 'max_amount',
      headerName: 'Max Amount (RMB)',
      width: 140,
      type: 'number',
      renderCell: (params) => {
        if (!params || !params.row) return 'No limit';
        const value = params.row.max_amount;
        if (value == null || value === '') return 'No limit';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return 'No limit';
        return numValue.toLocaleString();
      },
    },
    {
      field: 'rate',
      headerName: 'Rate (NGN)',
      width: 120,
      type: 'number',
      renderCell: (params) => {
        if (!params || !params.row) return '₦0.00';
        const value = params.row.rate;
        if (value == null || value === '') return '₦0.00';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '₦0.00';
        return `₦${numValue.toFixed(2)}`;
      },
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.is_active ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row) return 'N/A';
        const createdBy = params.row.created_by;
        if (!createdBy) return 'N/A';
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {createdBy.username || 'N/A'}
            </Typography>
            {createdBy.email && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                {createdBy.email}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleOpen(params.row)}
            sx={{ color: '#DC143C' }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setDeleteModal({ open: true, id: params.row.sid })}
            sx={{ color: '#DC143C' }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          RMB Exchange Rates
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            Add Rate
          </Button>
        </Box>
      </Box>
      
      {/* Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
                Filters
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Filter exchange rates by wallet type and status
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" color="textSecondary">
                Showing {rates.length} rate{rates.length !== 1 ? 's' : ''}
                {hasActiveFilters && (
                  <span> (filtered)</span>
                )}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                sx={{ borderColor: '#DC143C', color: '#DC143C' }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Wallet Type"
                value={filter.wallet_type}
                onChange={(e) => handleFilterChange('wallet_type', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Wallet Types</MenuItem>
                <MenuItem value="foreign">Foreign Alipay</MenuItem>
                <MenuItem value="chinese">Chinese Alipay</MenuItem>
                <MenuItem value="chinese_bank">Chinese Bank</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                label="Status"
                value={filter.is_active}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Exchange Rates Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {rates.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  No exchange rates found
                </Typography>
                <Button variant="outlined" onClick={handleRefresh}>
                  Refresh Data
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(rates, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1200,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Rows per page:
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder="Rows"
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? 'Edit Exchange Rate' : 'Add Exchange Rate'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Wallet Type"
              value={form.wallet_type}
              onChange={e => setForm({ ...form, wallet_type: e.target.value })}
              select
              fullWidth
              required
            >
              <MenuItem value="foreign">Foreign Alipay Wallet</MenuItem>
              <MenuItem value="chinese">Chinese Alipay Wallet</MenuItem>
              <MenuItem value="chinese_bank">Chinese Bank Transfer</MenuItem>
            </TextField>
            <TextField
              label="Minimum Amount (RMB)"
              type="number"
              value={form.min_amount}
              onChange={e => setForm({ ...form, min_amount: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Maximum Amount (RMB)"
              type="number"
              value={form.max_amount}
              onChange={e => setForm({ ...form, max_amount: e.target.value })}
              fullWidth
              helperText="Leave empty for no maximum limit"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Exchange Rate (NGN per 1 RMB)"
              type="number"
              value={form.rate}
              onChange={e => setForm({ ...form, rate: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 0, step: 0.01 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {editId ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this exchange rate?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal({ open: false, id: null })}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteModal.id)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExchangeRate;
